<script setup lang="ts">
import { computed, useSlots } from 'vue';
import type { Component } from 'vue';

type Variant = 'primary' | 'secondary' | 'ghost' | 'soft' | 'danger' | 'subtle';
type Size = 'sm' | 'md' | 'lg';
type IconPosition = 'start' | 'end';

const props = withDefaults(
  defineProps<{
    as?: string | Component;
    variant?: Variant;
    size?: Size;
    block?: boolean;
    icon?: Component;
    iconPosition?: IconPosition;
    iconSize?: number | string;
    iconStrokeWidth?: number | string;
    unstyled?: boolean;
  }>(),
  {
    as: 'button',
    variant: 'primary',
    size: 'md',
    block: false,
    icon: undefined,
    iconPosition: 'start',
    iconSize: undefined,
    iconStrokeWidth: undefined,
    unstyled: false
  }
);

const slots = useSlots();
const hasIcon = computed(() => Boolean(props.icon) || Boolean(slots.icon));
const buttonClasses = computed(() =>
  props.unstyled
    ? undefined
    : [
        'ui-button',
        `ui-button--${props.size}`,
        `ui-button--${props.variant}`,
        hasIcon.value && `ui-button--icon-${props.iconPosition}`,
        { 'ui-button--block': props.block }
      ]
);
const resolvedIconSize = computed(() => {
  if (props.iconSize !== undefined) return props.iconSize;
  return {
    sm: 16,
    md: 18,
    lg: 20
  }[props.size];
});
</script>

<template>
  <component
    :is="as"
    v-bind="$attrs"
    :class="buttonClasses"
  >
    <span
      v-if="hasIcon && iconPosition === 'start'"
      class="ui-button__icon"
      aria-hidden="true"
    >
      <slot name="icon">
        <component
          :is="icon"
          v-if="icon"
          :size="resolvedIconSize"
          :stroke-width="iconStrokeWidth"
        />
      </slot>
    </span>

    <slot />

    <span
      v-if="hasIcon && iconPosition === 'end'"
      class="ui-button__icon"
      aria-hidden="true"
    >
      <slot name="icon">
        <component
          :is="icon"
          v-if="icon"
          :size="resolvedIconSize"
          :stroke-width="iconStrokeWidth"
        />
      </slot>
    </span>
  </component>
</template>

<style scoped>
.ui-button {
  --ui-button-bg: transparent;
  --ui-button-hover-bg: transparent;
  --ui-button-border: transparent;
  --ui-button-hover-border: transparent;
  --ui-button-border-style: solid;
  --ui-button-color: var(--ui-foreground);
  --ui-button-hover-color: var(--ui-foreground);
  --ui-button-shadow: none;
  --ui-button-hover-shadow: none;
  --ui-button-hover-filter: none;
  --ui-button-icon-size: 1.125rem;
  --ui-button-before-opacity: 0;

  position: relative;
  isolation: isolate;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--ui-space-2);
  overflow: hidden;
  border: 1px var(--ui-button-border-style) var(--ui-button-border);
  border-radius: var(--ui-control-radius);
  background: var(--ui-button-bg);
  box-shadow: var(--ui-button-shadow);
  color: var(--ui-button-color);
  cursor: pointer;
  font-weight: 720;
  letter-spacing: -0.005em;
  line-height: 1.25;
  text-decoration: none;
  transition:
    background 0.18s ease,
    background-color 0.18s ease,
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    color 0.18s ease,
    filter 0.18s ease,
    transform 0.18s ease;
  user-select: none;
  -webkit-user-select: none;
  appearance: none;
}

.ui-button::before {
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: inherit;
  background: linear-gradient(180deg, color-mix(in srgb, var(--ui-foreground-inverse) 18%, transparent), transparent 72%);
  content: '';
  opacity: var(--ui-button-before-opacity);
  pointer-events: none;
  transition: opacity 0.18s ease;
}

.ui-button:not(:disabled):hover {
  border-color: var(--ui-button-hover-border);
  background: var(--ui-button-hover-bg);
  box-shadow: var(--ui-button-hover-shadow);
  color: var(--ui-button-hover-color);
  filter: var(--ui-button-hover-filter);
  transform: translateY(-1px);
}

.ui-button:not(:disabled):hover::before {
  opacity: calc(var(--ui-button-before-opacity) + 0.1);
}

.ui-button:not(:disabled):active {
  box-shadow: var(--ui-button-shadow);
  transform: translateY(0);
}

.ui-button--block {
  width: 100%;
}

.ui-button--sm {
  --ui-button-icon-size: 1rem;

  min-height: 2.375rem;
  padding: 0.5rem 0.8rem;
  font-size: 0.75rem;
}

.ui-button--md {
  --ui-button-icon-size: 1.125rem;

  min-height: 2.75rem;
  padding: 0.6875rem 1.0625rem;
  font-size: 0.875rem;
}

.ui-button--lg {
  --ui-button-icon-size: 1.25rem;

  min-height: 3.125rem;
  padding: 0.8125rem 1.35rem;
  font-size: 1rem;
}

.ui-button__icon {
  display: inline-flex;
  width: var(--ui-button-icon-size);
  height: var(--ui-button-icon-size);
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
}

.ui-button__icon :deep(svg),
.ui-button :slotted(svg) {
  width: var(--ui-button-icon-size);
  height: var(--ui-button-icon-size);
  flex: 0 0 auto;
}

.ui-button:focus-visible {
  outline: none;
  box-shadow: var(--ui-button-shadow), var(--ui-focus-ring);
}

.ui-button:disabled {
  opacity: 0.58;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.ui-button--primary {
  --ui-button-fill: color-mix(in srgb, var(--ui-accent) 55%, #000);
  --ui-button-bg:
    linear-gradient(180deg, color-mix(in srgb, var(--ui-button-fill) 88%, var(--ui-foreground-inverse) 12%), var(--ui-button-fill));
  --ui-button-border: color-mix(in srgb, var(--ui-accent) 82%, var(--ui-border) 18%);
  --ui-button-color: var(--ui-on-accent);
  --ui-button-hover-bg:
    linear-gradient(180deg, color-mix(in srgb, var(--ui-button-fill) 94%, var(--ui-foreground-inverse) 6%), color-mix(in srgb, var(--ui-button-fill) 90%, var(--ui-foreground) 10%));
  --ui-button-hover-border: color-mix(in srgb, var(--ui-accent) 88%, var(--ui-foreground) 12%);
  --ui-button-hover-color: var(--ui-on-accent);
  --ui-button-shadow:
    0 1px 2px color-mix(in srgb, var(--ui-accent) 24%, transparent),
    0 12px 28px color-mix(in srgb, var(--ui-accent) 16%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--ui-on-accent) 22%, transparent);
  --ui-button-hover-shadow:
    0 1px 2px color-mix(in srgb, var(--ui-accent) 24%, transparent),
    0 16px 34px color-mix(in srgb, var(--ui-accent) 22%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--ui-on-accent) 24%, transparent);
  --ui-button-before-opacity: 0.34;
}

.ui-button--secondary {
  --ui-button-bg: linear-gradient(180deg, var(--ui-card-bg), color-mix(in srgb, var(--ui-card-bg-muted) 58%, var(--ui-card-bg)));
  --ui-button-border: var(--ui-border);
  --ui-button-color: var(--ui-foreground);
  --ui-button-hover-bg: linear-gradient(180deg, var(--ui-card-bg), var(--ui-card-bg-muted));
  --ui-button-hover-border: var(--ui-border-strong);
  --ui-button-shadow:
    0 1px 2px color-mix(in srgb, var(--ui-foreground) 7%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--ui-foreground-inverse) 34%, transparent);
  --ui-button-hover-shadow: var(--ui-control-shadow);
}

.ui-button--ghost {
  --ui-button-bg: transparent;
  --ui-button-border: transparent;
  --ui-button-color: var(--ui-foreground);
  --ui-button-hover-bg: color-mix(in srgb, var(--ui-surface-muted) 74%, transparent);
  --ui-button-hover-border: color-mix(in srgb, var(--ui-border) 78%, transparent);
}

.ui-button--soft {
  --ui-button-bg: color-mix(in srgb, var(--ui-accent) 10%, var(--ui-card-bg));
  --ui-button-border: color-mix(in srgb, var(--ui-accent) 24%, var(--ui-border));
  --ui-button-color: color-mix(in srgb, var(--ui-foreground) 78%, var(--ui-accent) 22%);
  --ui-button-hover-bg: color-mix(in srgb, var(--ui-accent) 14%, var(--ui-card-bg));
  --ui-button-hover-border: color-mix(in srgb, var(--ui-accent) 34%, var(--ui-border));
  --ui-button-shadow: inset 0 1px 0 color-mix(in srgb, var(--ui-foreground-inverse) 24%, transparent);
}

.ui-button--subtle {
  --ui-button-bg: transparent;
  --ui-button-border: color-mix(in srgb, var(--ui-border) 86%, transparent);
  --ui-button-border-style: dashed;
  --ui-button-color: var(--ui-foreground);
  --ui-button-hover-bg: color-mix(in srgb, var(--ui-surface-muted) 74%, transparent);
  --ui-button-hover-border: var(--ui-border-strong);
}

.ui-button--danger {
  --ui-button-fill: color-mix(in srgb, var(--ui-danger) 65%, #000);
  --ui-button-bg:
    linear-gradient(180deg, color-mix(in srgb, var(--ui-button-fill) 88%, var(--ui-foreground-inverse) 12%), var(--ui-button-fill));
  --ui-button-border: var(--ui-danger);
  --ui-button-color: var(--ui-on-danger);
  --ui-button-hover-bg:
    linear-gradient(180deg, var(--ui-button-fill), color-mix(in srgb, var(--ui-button-fill) 86%, var(--ui-foreground) 14%));
  --ui-button-hover-border: color-mix(in srgb, var(--ui-danger) 86%, var(--ui-foreground) 14%);
  --ui-button-hover-color: var(--ui-on-danger);
  --ui-button-shadow:
    0 1px 2px color-mix(in srgb, var(--ui-danger) 20%, transparent),
    0 12px 28px color-mix(in srgb, var(--ui-danger) 14%, transparent);
  --ui-button-hover-shadow: 0 16px 34px color-mix(in srgb, var(--ui-danger) 20%, transparent);
  --ui-button-before-opacity: 0.26;
}

.ui-button--danger:active:not(:disabled) {
  transform: translateY(1px);
}

@media (prefers-reduced-motion: reduce) {
  .ui-button,
  .ui-button::before {
    transition: none;
  }
}

@media (pointer: coarse) {
  .ui-button--sm {
    min-height: var(--ui-touch-target-min);
  }
}

:global([data-theme='dark']) .ui-button--primary {
  --ui-button-fill: var(--ui-accent);
}

:global([data-theme='dark']) .ui-button--secondary {
  --ui-button-hover-bg: linear-gradient(180deg, var(--ui-card-bg-hover), var(--ui-card-bg-muted));
}
</style>
